// Композер-вью (SPEC §4.2): связывает useComposer/useVoiceComposer с design-system InputBar +
// эмодзи-пикер (Sheet) + автокомплит упоминаний. Морфинг record/send — по composer.mode. Жесты
// микрофона 1:1 в рекордер. Презентация — InputBar; вся логика — в хуках композера.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { Chat, MessageRef, UserId } from '@tele-chat/shared-contracts'
import { MESSAGE_TEXT_MAX } from '@tele-chat/shared-contracts'
import { InputBar, Sheet, useLayout } from '@tele-chat/design-system'
import type { ComposerApi } from '../chat/useComposer.js'
import { readClipboard, clipboardReadSupported } from '../chat/useFileIntake.js'
import { useDiag } from '../app/DiagContext.js'
import { useVoiceComposer } from '../chat/useVoiceComposer.js'
import { useUserCache } from '../users/UserCacheContext.js'
import { useNotifications } from '../notifications/NotificationsContext.js'
import { strings } from '../i18n/strings.js'

/** Небольшой набор частых эмодзи для пикера (нативные символы — без ассетов). */
const EMOJI_SET = [
  '😀', '😁', '😂', '🤣', '😊', '😍', '😘', '😎', '🤔', '😐',
  '😢', '😭', '😡', '👍', '👎', '👏', '🙏', '💪', '🔥', '🎉',
  '❤️', '💔', '✅', '❌', '⭐', '☕', '🚀', '📌', '📎', '🎤',
]

export interface ComposerProps {
  chat: Chat
  composer: ComposerApi
}

export function Composer({ chat, composer }: ComposerProps): JSX.Element {
  const voice = useVoiceComposer(chat.id)
  const users = useUserCache()
  const { notify } = useNotifications()
  const diag = useDiag()
  const layout = useLayout()
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)

  // Кнопка «Вставить из буфера» — только на mobile и только где есть async Clipboard API. Нужна прежде
  // всего для iOS: нативная вставка в поле файл не отдаёт (WebKit, <textarea>). На desktop обходятся
  // Ctrl+V/перетаскиванием, на Android — нативной вставкой, поэтому там кнопку не показываем.
  const showPasteButton = layout === 'mobile' && clipboardReadSupported()
  const onPasteClipboard = useCallback(async (): Promise<void> => {
    try {
      const { files, types } = await readClipboard()
      if (files.length > 0) {
        composer.addFiles(files)
        return
      }
      // Пусто. Главный кейс (подтверждён client-diag 06.07): фото из «Галереи» iOS — система НЕ отдаёт
      // его в веб-буфер (types="(none)"), тогда как скриншоты/копии из Safari отдаёт (image/png).
      // Извлечь нельзя ничем — подсказываем скрепку (в iOS-пикере есть «Медиатека»). Маркер батчится.
      diag.log('clipboard.empty', { types: types.join(',') || '(none)' })
      notify({ kind: 'info', title: strings.composer.clipboardEmpty })
    } catch (err) {
      const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
      // Отказ в доступе к буферу (пользователь отменил iOS-callout) — тихо; прочее логируем+тост.
      if (!/not allowed|denied/i.test(message)) {
        diag.log('clipboard.error', { err: message })
        notify({ kind: 'info', title: strings.composer.clipboardEmpty })
      }
    }
  }, [composer, notify, diag])

  // Сбой записи/отправки голосового — тостом. Без этого любой отказ рекордера (пустая запись,
  // прерывание, отказ микрофона, сбой загрузки) выглядел как «индикатор исчез без последствий» —
  // так и потерялись голосовые с телефона в демо 02.07. Тексты уже русские (voice-recorder fail()).
  const lastVoiceErrorRef = useRef<typeof voice.snapshot.error>(undefined)
  useEffect(() => {
    const err = voice.snapshot.error
    if (err !== undefined && err !== lastVoiceErrorRef.current) {
      notify({ kind: 'error', title: err.message })
    }
    lastVoiceErrorRef.current = err
  }, [voice.snapshot.error, notify])

  // Превью reply/edit как MessageRef для InputBar.
  const replyRef: MessageRef | undefined = composer.replyingTo ?? undefined
  const editRef: MessageRef | undefined = composer.editing ?? undefined

  // Кандидаты упоминаний — участники группы (по memberIds), профили из кеша.
  const mentionCandidates = useMemo(() => {
    if (chat.type !== 'group' || mentionQuery === null) return []
    const q = mentionQuery.toLowerCase()
    return chat.memberIds
      .map((id) => users.get(id))
      .filter((u): u is NonNullable<typeof u> => u !== undefined)
      .filter((u) => u.name.toLowerCase().includes(q))
      .slice(0, 8)
  }, [chat, mentionQuery, users])

  const onTextChange = (value: string): void => {
    composer.setText(value)
    // Открываем автокомплит упоминаний при наборе «@…» в группе.
    if (chat.type === 'group') {
      const m = /@([\w.-]*)$/.exec(value)
      setMentionQuery(m ? (m[1] ?? '') : null)
      // Гарантируем, что профили участников подгружены для подсказок.
      for (const id of chat.memberIds) if (users.get(id) === undefined) users.ensure(id)
    }
  }

  const onFiles = (fileList: FileList | null): void => {
    if (fileList === null) return
    composer.addFiles(Array.from(fileList))
  }

  // Скрытый file-input для скрепки.
  const fileInputId = `composer-file-${chat.id}`

  const mentionBox: CSSProperties = {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    background: 'var(--color-surface)',
    boxShadow: 'var(--shadow-menu)',
    borderRadius: 'var(--radius-card)',
    maxHeight: '200px',
    overflowY: 'auto',
    margin: 'var(--space-2)',
  }

  return (
    <div data-composer="" style={{ position: 'relative' }}>
      {mentionCandidates.length > 0 ? (
        <div data-mention-list="" style={mentionBox}>
          {mentionCandidates.map((u: { id: UserId; name: string }) => (
            <button
              key={u.id}
              type="button"
              data-mention-option={u.id}
              onClick={() => {
                composer.addMention(u.id, u.name.replace(/\s+/g, ''))
                setMentionQuery(null)
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: 'transparent',
                padding: 'var(--space-2) var(--space-3)',
                color: 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              {u.name}
            </button>
          ))}
        </div>
      ) : null}

      <input
        id={fileInputId}
        type="file"
        multiple
        data-composer-file-input=""
        style={{ display: 'none' }}
        onChange={(e) => onFiles(e.target.files)}
      />

      <InputBar
        value={composer.text}
        onChange={onTextChange}
        onSend={() => {
          // Инвариант длины текста (сервер всё равно отвергнет VALIDATION) — говорим сразу,
          // не отправляя: вставка гигантского дампа роняла рендер у всех участников чата.
          if (composer.text.length > MESSAGE_TEXT_MAX) {
            notify({ kind: 'error', title: strings.errors.messageTooLong(MESSAGE_TEXT_MAX) })
            return
          }
          composer.submit()
        }}
        onAttach={() => document.getElementById(fileInputId)?.click()}
        onPasteClipboard={showPasteButton ? () => void onPasteClipboard() : undefined}
        onEmoji={() => setEmojiOpen(true)}
        mode={composer.mode}
        recording={voice.snapshot.state === 'recording' ? { elapsedMs: voice.snapshot.elapsedMs } : undefined}
        onMicPressStart={voice.pressStart}
        onMicPressEnd={voice.pressEnd}
        onMicCancel={voice.pressCancel}
        replyingTo={replyRef}
        onCancelReply={composer.cancelReply}
        editing={editRef}
        onCancelEdit={composer.cancelEdit}
        uploads={composer.pending.map((p) => ({
          slotId: p.slotId,
          filename: p.filename,
          kind: p.kind,
          progress: p.progress,
          ready: p.attachment !== undefined,
          error: p.error !== undefined,
          // UPQ-4: очередь/ожидание сети видны на плашке (ready/error передаются флагами выше)
          phase: p.status === 'queued' || p.status === 'uploading' || p.status === 'waiting' ? p.status : undefined,
        }))}
        onCancelUpload={composer.removeAttachment}
        onRetryUpload={composer.retryAttachment}
        placeholder={strings.composer.placeholder}
      />

      <Sheet open={emojiOpen} onClose={() => setEmojiOpen(false)} label={strings.composer.placeholder}>
        <div
          data-emoji-picker=""
          style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 'var(--space-2)' }}
        >
          {EMOJI_SET.map((emoji) => (
            <button
              key={emoji}
              type="button"
              data-emoji={emoji}
              onClick={() => {
                composer.insertEmoji(emoji)
                setEmojiOpen(false)
              }}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: 'var(--font-xl)',
                cursor: 'pointer',
                padding: 'var(--space-1)',
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      </Sheet>
    </div>
  )
}
